import { useRef, useState } from "react";
import { uploadAdminImage, ApiError } from "../../api/client";
import { useToast } from "./Toast";

/**
 * Round 56 (6B) - "Upload" helper that sits under an existing URL field. The file goes to POST /admin/media/upload, where the
 * Worker checks the real file type and size, resizes/compresses according to `kind`, and returns the public URL, which is
 * handed to onUploaded (the page puts it into the same URL field it already saved, so nothing about saving changes).
 * Pasting a URL by hand still works.
 */
export function UploadButton({ kind, onUploaded, accept = "image/png,image/jpeg,image/webp,image/gif", label = "Upload file" }: {
  kind: string;
  onUploaded: (url: string) => void;
  accept?: string;
  label?: string;
}) {
  const { showToast } = useToast();
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  async function pick(f?: File) {
    if (!f) return;
    setBusy(true);
    try {
      const r = await uploadAdminImage(f, kind);
      onUploaded(r.url);
      showToast(r.optimized ? "Uploaded and optimized" : "Uploaded", "success");
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : "Upload failed", "error");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  }
  return (
    <div className="-mt-1 flex items-center gap-2">
      <input ref={ref} type="file" accept={accept} disabled={busy} onChange={(e) => pick(e.target.files?.[0])} className="hidden" />
      <button type="button" disabled={busy} onClick={() => ref.current?.click()}
        className="focus-ring rounded-lg border border-surface-border bg-surface-card px-3 py-1 text-xs font-medium text-ink hover:bg-surface-raised disabled:opacity-50">
        {busy ? "Uploading…" : label}
      </button>
      <span className="text-[11px] text-ink-faint">or paste a URL above</span>
    </div>
  );
}
